// src/components/broker/BrokerLayout.tsx
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';

interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  plan: string;
  company_code: string;
  subscription_status?: string;
  trial_ends_at?: string;
}

export default function BrokerLayout() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingDocs, setPendingDocs] = useState(0);
  const [pendingCarriers, setPendingCarriers] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [msgRecipient, setMsgRecipient] = useState<'all' | 'specific'>('all');
  const [msgText, setMsgText] = useState('');
  const [msgTitle, setMsgTitle] = useState('');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [carriers, setCarriers] = useState<{id: string; name: string; email: string}[]>([]);
  const [selectedCarrierIds, setSelectedCarrierIds] = useState<string[]>([]);

  useEffect(() => {
    checkBrokerAccess();
  }, []);

  const checkBrokerAccess = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        navigate('/login');
        return;
      }

      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) throw error;

      if (profileData.role !== 'broker') {
        navigate('/carrier/dashboard');
        return;
      }

      setProfile(profileData);

      // Fetch pending document count
      const { data: docs } = await supabase
        .from('documents')
        .select('id, user_id')
        .eq('status', 'pending');

      if (docs) {
        const { data: apps } = await supabase
          .from('applications')
          .select('user_id, status')
          .eq('company_code', profileData.company_code);

        if (apps) {
          const carrierIds = apps.map(a => a.user_id);
          const pendingCount = docs.filter(d => carrierIds.includes(d.user_id)).length;
          setPendingDocs(pendingCount);

          // Count carriers with pending/submitted applications
          const pendingApps = apps.filter(a => a.status === 'submitted' || a.status === 'pending').length;
          setPendingCarriers(pendingApps);
        }
      }

      // Fetch notifications - includes both company notifications AND admin messages sent to this broker
      const { data: notifs } = await supabase
        .from('notifications')
        .select('*')
        .or(`company_code.eq.${profileData.company_code},and(recipient_id.eq.${session.user.id},type.eq.admin_message)`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (notifs) {
        // Filter out carrier-targeted messages (broker shouldn't see broker_message type meant for carriers)
        const brokerNotifs = notifs.filter((n: any) => n.type !== 'broker_message' || n.recipient_id === session.user.id);
        setNotifications(brokerNotifs);
        setUnreadCount(brokerNotifs.filter((n: any) => !n.read).length);
      }
    } catch (error) {
      console.error('Error checking broker access:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const fetchCarriers = async () => {
    if (!profile?.company_code) return;
    const { data } = await supabase
      .from('applications')
      .select('user_id')
      .eq('company_code', profile.company_code);
    if (data) {
      const userIds = data.map(a => a.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .in('id', userIds);
      setCarriers((profiles || []).map(p => ({
        id: p.id,
        name: `${p.first_name} ${p.last_name}`,
        email: p.email,
      })));
    }
  };

  const handleSendMessage = async () => {
    // Guard against double-click / React StrictMode double-fire
    if (sendingMsg) return;
    if (!msgTitle.trim() || !msgText.trim()) return;
    setSendingMsg(true);
    try {
      let recipientIds: string[] = [];

      if (msgRecipient === 'all') {
        const { data: apps } = await supabase
          .from('applications')
          .select('user_id')
          .eq('company_code', profile?.company_code);
        recipientIds = (apps || []).map(a => a.user_id);
      } else {
        recipientIds = selectedCarrierIds;
      }

      if (recipientIds.length === 0) {
        alert('No carriers found to send to.');
        return;
      }

      const notificationsToInsert = recipientIds.map(uid => ({
        company_code: profile?.company_code,
        recipient_id: uid,
        recipient_role: 'carrier',
        title: msgTitle,
        message: msgText,
        type: 'broker_message',
      }));

      await supabase.from('notifications').insert(notificationsToInsert);

      setShowMessageModal(false);
      setMsgTitle('');
      setMsgText('');
      setSelectedCarrierIds([]);
      alert('Message sent!');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message.');
    } finally {
      setSendingMsg(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `group flex items-center px-3 py-2 text-base font-medium rounded-md mt-1 ${
      isActive
        ? 'bg-blue-100 text-blue-700'
        : 'text-gray-900 hover:bg-gray-50'
    }`;

  const iconClass = (isActive: boolean) =>
    `mr-3 h-5 w-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
             <Link to="/broker/dashboard" className="flex items-center">
                <img src="/images/loaddrive-logo.png" alt="LoadDrive" className="h-8" />
              </Link>
              <span className="ml-3 text-sm text-gray-500">Broker Portal</span>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <button onClick={() => setShowNotifications(!showNotifications)} className="relative p-2 text-gray-500 hover:text-gray-700">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{unreadCount}</span>
                  )}
                </button>
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50 max-h-96 overflow-y-auto">
                    <div className="p-3 border-b flex justify-between items-center">
                      <h3 className="font-semibold text-sm">Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={async () => {
                          const ids = notifications.filter(n => !n.read).map(n => n.id);
                          if (ids.length > 0) await supabase.from('notifications').update({ read: true }).in('id', ids);
                          setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                          setUnreadCount(0);
                        }} className="text-xs text-blue-600 hover:underline">Mark all read</button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">No notifications</div>
                    ) : (
                      notifications.map(n => (
                        <div key={n.id} className={`p-3 border-b text-sm hover:bg-gray-50 cursor-pointer ${!n.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'border-l-4 border-l-transparent'}`}
                          onClick={async () => {
                            if (!n.read) {
                              await supabase.from('notifications').update({ read: true }).eq('id', n.id);
                              setNotifications(prev => prev.map(notif => notif.id === n.id ? { ...notif, read: true } : notif));
                              setUnreadCount(prev => Math.max(0, prev - 1));
                            }
                            setShowNotifications(false);
                            if (n.type === 'admin_message') {
                              // Admin messages don't navigate anywhere specific
                            } else if (n.type === 'signed' || n.type === 'declined') {
                              navigate('/broker/agreements');
                            } else if (n.type === 'carrier_joined') {
                              navigate('/broker/carriers');
                            } else if (n.type === 'docs_submitted') {
                              navigate('/broker/documents');
                            } else {
                              navigate('/broker/carriers');
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              n.type === 'admin_message' ? 'bg-yellow-500'
                                : n.type === 'signed' ? 'bg-green-500'
                                : n.type === 'declined' ? 'bg-red-500'
                                : 'bg-blue-500'
                            }`} />
                            <p className="font-medium text-gray-900">
                              {n.type === 'admin_message' && <span className="text-yellow-600 text-xs mr-1">ADMIN</span>}
                              {n.title}
                            </p>
                          </div>
                          <p className="text-gray-600 mt-1 ml-4">{n.message}</p>
                          <p className="text-xs text-gray-400 mt-1 ml-4">{new Date(n.created_at).toLocaleString()}</p>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              <span className="text-sm text-gray-700">
                {profile?.first_name} {profile?.last_name}
              </span>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 bg-white shadow-sm min-h-[calc(100vh-4rem)]">
          <nav className="mt-5 px-2">
            <NavLink to="/broker/dashboard" end className={navLinkClass}>
              {({ isActive }) => (
                <>
                  <svg className={iconClass(isActive)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Dashboard
                </>
              )}
            </NavLink>

            <NavLink to="/broker/carriers" className={navLinkClass}>
              {({ isActive }) => (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <svg className={iconClass(isActive)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    Carriers
                  </div>
                  {pendingCarriers > 0 && (
                    <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                      {pendingCarriers}
                    </span>
                  )}
                </div>
              )}
            </NavLink>

            <NavLink to="/broker/documents" className={navLinkClass}>
              {({ isActive }) => (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <svg className={iconClass(isActive)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Documents
                  </div>
                  {pendingDocs > 0 && (
                    <span className="bg-yellow-500 text-white text-xs rounded-full px-2 py-0.5">
                      {pendingDocs}
                    </span>
                  )}
                </div>
              )}
            </NavLink>

            <NavLink to="/broker/agreements" className={navLinkClass}>
              {({ isActive }) => (
                <>
                  <svg className={iconClass(isActive)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Rate Cons & Agreements
                 
                </>
              )}
            </NavLink>

            <button
              onClick={() => setShowMessageModal(true)}
              className="group flex items-center px-3 py-2 text-base font-medium rounded-md mt-1 text-gray-900 hover:bg-gray-50 w-full"
            >
              <svg className="mr-3 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              Send Message
            </button>

            <NavLink to="/broker/settings" className={navLinkClass}>
              {({ isActive }: { isActive: boolean }) => (
                <>
                  <svg className={iconClass(isActive)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Settings
                </>
              )}
            </NavLink>

            {/*profile?.subscription_status !== 'active' && (
              <Link
                to="/pricing?plan=starter"
                className="group flex items-center px-3 py-2 text-sm font-semibold rounded-md bg-yellow-50 text-yellow-700 hover:bg-yellow-100 mt-4 border-2 border-yellow-200"
              >
                <svg className="mr-3 h-5 w-5 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                {profile?.subscription_status === 'expired' ? '⚠️ Upgrade Now' : 'Upgrade Plan'}
              </Link>
            )*/}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8">
          <Outlet context={{ profile }} />
        </main>
      </div>

      {/* Send Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Send Message to Carriers</h3>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
              <select
                value={msgRecipient}
                onChange={(e) => {
                  setMsgRecipient(e.target.value as 'all' | 'specific');
                  if (e.target.value === 'specific' && carriers.length === 0) fetchCarriers();
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="all">All Carriers</option>
                <option value="specific">Specific Carriers</option>
              </select>
            </div>
            {msgRecipient === 'specific' && (
              <div className="mb-3 max-h-32 overflow-y-auto border rounded-md p-2">
                {carriers.length === 0 ? (
                  <p className="text-xs text-gray-400">Loading carriers...</p>
                ) : (
                  carriers.map(c => (
                    <label key={c.id} className="flex items-center gap-2 py-1 text-sm cursor-pointer hover:bg-gray-50 px-1 rounded">
                      <input
                        type="checkbox"
                        checked={selectedCarrierIds.includes(c.id)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedCarrierIds(prev => [...prev, c.id]);
                          else setSelectedCarrierIds(prev => prev.filter(id => id !== c.id));
                        }}
                      />
                      {c.name} <span className="text-xs text-gray-400">({c.email})</span>
                    </label>
                  ))
                )}
              </div>
            )}
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                value={msgTitle}
                onChange={(e) => setMsgTitle(e.target.value)}
                placeholder="e.g. Insurance Reminder"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={msgText}
                onChange={(e) => setMsgText(e.target.value)}
                placeholder="Type your message..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowMessageModal(false); setMsgTitle(''); setMsgText(''); setSelectedCarrierIds([]); }} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm">Cancel</button>
              <button
                onClick={handleSendMessage}
                disabled={!msgTitle.trim() || !msgText.trim() || sendingMsg || (msgRecipient === 'specific' && selectedCarrierIds.length === 0)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
              >
                {sendingMsg ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}